// The floating bar at the bottom of the map in select mode: the box tool and "Select all 4,734" (every store the
// filters let onto the map, not only the ones in view, so the count is in the label) on one side, and, once
// something is picked, the bulk bar ("12 selected" · "Assign to…" · "Clear"). The two halves wrap onto two rows on a
// phone, so the bar never runs wider than the map. A plain group, not a toolbar: it has no arrow-key navigation.

import { SquareDashedMousePointer, UserRoundCheck } from 'lucide-react';
import type { Ref } from 'react';
import { formatGrouped } from '@/domain/format';
import { Button, IconButton, cn, useMediaQuery } from '@/ui';
import { selectAllText, selectedText } from './mapSelection';

export interface MapSelectionBarProps {
  /** Selected stores on the map now. */
  count: number;
  /** Stores on the map with the current filters ("Select all shown"). */
  shownCount: number;
  /** Every drag draws a box (else Shift-drag does). */
  boxTool: boolean;
  onBoxToolChange: (on: boolean) => void;
  onSelectAll: () => void;
  onClear: () => void;
  /** Absent when the viewer cannot assign owners. */
  onAssign?: () => void;
  /** The box tool button (always there), where focus goes back when "Assign to…" goes away after an assign. */
  boxToolRef?: Ref<HTMLButtonElement>;
  className?: string;
}

export function MapSelectionBar({
  count,
  shownCount,
  boxTool,
  onBoxToolChange,
  onSelectAll,
  onClear,
  onAssign,
  boxToolRef,
  className,
}: MapSelectionBarProps) {
  // Touch screens have no Shift key: point people at the box tool instead.
  const coarse = useMediaQuery('(pointer: coarse)');
  const allSelected = shownCount > 0 && count === shownCount;
  const hint = boxTool
    ? 'Drag on the map to select an area'
    : coarse
      ? 'Tap pins to select them, or use the box tool'
      : 'Click pins to select them, or Shift-drag an area';

  return (
    <div
      role="group"
      aria-label="Selection"
      className={cn(
        'pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-x-3 gap-y-1.5 rounded-tile border border-line bg-surface/95 p-1.5 shadow-raised backdrop-blur',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-1">
        {/* A constant name with aria-pressed: "Draw a box to select, pressed" says the tool is on. */}
        <IconButton
          ref={boxToolRef}
          label="Draw a box to select"
          icon={<SquareDashedMousePointer />}
          size="sm"
          pressed={boxTool}
          onClick={() => onBoxToolChange(!boxTool)}
        />
        <Button
          variant="ghost"
          size="sm"
          disabled={shownCount === 0 || allSelected}
          title={`Select all ${formatGrouped(shownCount)} ${shownCount === 1 ? 'store' : 'stores'} on the map with the current filters`}
          onClick={onSelectAll}
        >
          {selectAllText(shownCount)}
        </Button>
      </div>
      {count === 0 ? (
        <p className="min-w-0 px-1 text-xs text-fg-muted">{hint}</p>
      ) : (
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="px-1 text-[13px] font-semibold whitespace-nowrap text-fg tabular-nums">{selectedText(count)}</span>
          {onAssign && (
            <Button variant="primary" size="sm" icon={<UserRoundCheck aria-hidden />} onClick={onAssign}>
              Assign to…
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear
          </Button>
        </div>
      )}
      <span className="sr-only" aria-live="polite">
        {count === 0 ? 'No stores selected' : selectedText(count)}
      </span>
    </div>
  );
}
